/**
 * Identity Manager - User identity and key management
 */

import nacl from 'tweetnacl';
import util from 'tweetnacl-util';

export class IdentityManager {
  constructor(client) {
    this.client = client;
    this.keyPair = null;
    this.userId = null;
  }

  /**
   * Initialize identity manager
   */
  async initialize() {
    // Try to load existing keys
    const keys = await this.client.storage.get('identity_keys');
    if (keys) {
      this.keyPair = {
        publicKey: util.decodeBase64(keys.publicKey),
        secretKey: util.decodeBase64(keys.secretKey),
      };
      this.userId = await this.client.storage.get('user_id');
    }
  }

  /**
   * Generate new key pair
   */
  generateKeyPair() {
    this.keyPair = nacl.sign.keyPair();
    return this.keyPair;
  }

  /**
   * Register a new user
   */
  async register(username) {
    // Generate keys if not exists
    if (!this.keyPair) {
      this.generateKeyPair();
    }

    // Convert to hex format (server expects hex, not base64)
    const publicKeyHex = Array.from(this.keyPair.publicKey)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    // Sign registration data
    const signature = this._sign(username + Date.now());

    try {
      const result = await this.client.network.apiPost('/api/v1/register', {
        username: username,
        public_key: publicKeyHex,
        signature: signature,
        device_id: this._getDeviceId(),
      });

      if (result.success) {
        // Save keys
        await this.client.storage.set('identity_keys', {
          publicKey: util.encodeBase64(this.keyPair.publicKey),
          secretKey: util.encodeBase64(this.keyPair.secretKey),
        });
        await this.client.storage.set('user_id', result.user_id);

        this.userId = result.user_id;
      }

      return result;
    } catch (error) {
      throw new Error(`Registration failed: ${error.message}`);
    }
  }

  /**
   * Login with existing credentials
   */
  async login(userId, token) {
    // Load keys from storage
    const keys = await this.client.storage.get('identity_keys');
    if (!keys) {
      throw new Error('No identity keys found');
    }

    this.keyPair = {
      publicKey: util.decodeBase64(keys.publicKey),
      secretKey: util.decodeBase64(keys.secretKey),
    };
    this.userId = userId;

    // Authenticate with server
    return this.client.authenticate(token);
  }

  /**
   * Get public key
   */
  getPublicKey() {
    if (!this.keyPair) return null;
    return util.encodeBase64(this.keyPair.publicKey);
  }

  /**
   * Sign data
   */
  sign(data) {
    if (!this.keyPair) {
      throw new Error('No key pair available');
    }

    const messageBytes = new TextEncoder().encode(data);
    const signature = nacl.sign.detached(messageBytes, this.keyPair.secretKey);
    return util.encodeBase64(signature);
  }

  /**
   * Verify signature
   */
  verify(data, signature, publicKey) {
    const messageBytes = new TextEncoder().encode(data);
    const signatureBytes = util.decodeBase64(signature);
    const publicKeyBytes = util.decodeBase64(publicKey);

    return nacl.sign.detached.verify(messageBytes, signatureBytes, publicKeyBytes);
  }

  /**
   * Export identity
   */
  async exportIdentity() {
    if (!this.keyPair) {
      throw new Error('No identity to export');
    }

    return {
      userId: this.userId,
      publicKey: util.encodeBase64(this.keyPair.publicKey),
      secretKey: util.encodeBase64(this.keyPair.secretKey),
    };
  }

  /**
   * Import identity
   */
  async importIdentity(identityData) {
    this.keyPair = {
      publicKey: util.decodeBase64(identityData.publicKey),
      secretKey: util.decodeBase64(identityData.secretKey),
    };
    this.userId = identityData.userId;

    await this.client.storage.set('identity_keys', {
      publicKey: identityData.publicKey,
      secretKey: identityData.secretKey,
    });
    await this.client.storage.set('user_id', identityData.userId);
  }

  _sign(data) {
    return this.sign(data);
  }

  _getDeviceId() {
    let deviceId = localStorage.getItem('im_device_id');
    if (!deviceId) {
      deviceId = 'web_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('im_device_id', deviceId);
    }
    return deviceId;
  }
}
